import React, { useEffect, useRef, useState } from "react";
import { setLayerConfig, setLayerEffect } from "../controllers/lightsParameters";
import ModalNumberInput from "./ModalNumberInput";
import ModalColorPicker from "./ModalColorPicker";
import "./LayerControl.css";

function LayerControl({ layer, index }) {
	const [collapsed, setCollapsed] = useState(true);
	const [colorPickerOpen, setColorPickerOpen] = useState(false);

	// Refs for throttled sending
	const lastCfgSentRef = useRef(0);
	const debounceCfgRef = useRef(null);
	useEffect(()=>()=>clearTimeout(debounceCfgRef.current),[]);

	// Get current color for display
	const currentColor = Array.isArray(layer.color) ? layer.color : [128, 128, 128];
	const colorStyle = {
		background: `rgb(${currentColor[0]}, ${currentColor[1]}, ${currentColor[2]})`,
		boxShadow: `0 0 12px rgba(${currentColor[0]}, ${currentColor[1]}, ${currentColor[2]}, 0.5)`,
	};

	// Send color change (called from modal)
	const handleColorChange = async (rgb) => {
		const minInterval = 100;
		const now = Date.now();
		const elapsed = now - lastCfgSentRef.current;

		const send = async () => {
			try {
				await setLayerConfig(
					index,
					layer.enabled ? 1 : 0,
					layer.startLED ?? 0,
					layer.endLED ?? 0,
					rgb[0],
					rgb[1],
					rgb[2]
				);
				lastCfgSentRef.current = Date.now();
			} catch (e) {
				console.error("Failed to send color", e);
			}
		};

		if (elapsed >= minInterval) {
			if (debounceCfgRef.current) clearTimeout(debounceCfgRef.current);
			send();
		} else {
			if (debounceCfgRef.current) clearTimeout(debounceCfgRef.current);
			debounceCfgRef.current = setTimeout(send, minInterval - elapsed + 10);
		}
	};

	const handleLayerConfigChange = async (newConfig) => {
		try {
			await setLayerConfig(
				index,
				newConfig.enabled ? 1 : 0,
				newConfig.startLED,
				newConfig.endLED,
				newConfig.color[0],
				newConfig.color[1],
				newConfig.color[2]
			);
		} catch (error) {
			console.error("Failed to send layer config", error);
		}
	};

	const handleLayerEffectChange = async (newEffect) => {
		try {
			await setLayerEffect(
				index,
				newEffect.effect ?? 0,
				newEffect.effectStrength ?? 0,
				newEffect.effectSpeed ?? 1
			);
		} catch (error) {
			console.error("Failed to send layer effect", error);
		}
	};

	const effects = [
		{ id: 0, name: "None" },
		{ id: 1, name: "Fire" },
		{ id: 2, name: "Rainbow" },
	];

	return (
		<div className="layer-control">
			<div
				className={`layer-header ${collapsed ? "" : "layer-header-open"}`}
				onClick={() => setCollapsed(!collapsed)}
			>
				<div className="layer-header-left">
					<span className={`layer-status ${layer.enabled ? "layer-status-on" : ""}`}></span>
					<span className="layer-title">Layer {index}</span>
				</div>
				<div className="layer-header-right">
					<div className="layer-color-preview" style={colorStyle}></div>
					<span className="layer-chevron">{collapsed ? "›" : "‹"}</span>
				</div>
			</div>

			{!collapsed && (
				<div className="layer-content">
					{/* Enable/Disable Toggle */}
					<div className="layer-row">
						<span className="layer-label">Enabled</span>
						<button
							className={`layer-toggle ${layer.enabled ? "layer-toggle-on" : ""}`}
							onClick={() => handleLayerConfigChange({ ...layer, enabled: !layer.enabled })}
						>
							<span className="layer-toggle-knob"></span>
						</button>
					</div>

					{/* LED Range */}
					<div className="layer-row">
						<span className="layer-label">LED Range</span>
						<div className="layer-range-inputs">
							<ModalNumberInput
								value={layer.startLED}
								min={0}
								max={136}
								step={1}
								title={`Layer ${index} Start LED`}
								onApply={(next) => handleLayerConfigChange({ ...layer, startLED: Math.round(next) })}
							/>
							<span className="layer-range-sep">–</span>
							<ModalNumberInput
								value={layer.endLED}
								min={0}
								max={136}
								step={1}
								title={`Layer ${index} End LED`}
								onApply={(next) => handleLayerConfigChange({ ...layer, endLED: Math.round(next) })}
							/>
						</div>
					</div>

					{/* Color Picker Button */}
					<div className="layer-row">
						<span className="layer-label">Color</span>
						<button
							className="layer-color-button"
							style={colorStyle}
							onClick={() => setColorPickerOpen(true)}
						>
							Edit
						</button>
					</div>

					{/* Effect Selection */}
					<div className="layer-section">
						<span className="layer-section-title">Effect</span>
						<div className="layer-effect-buttons">
							{effects.map((eff) => (
								<button
									key={eff.id}
									className={`layer-effect-btn ${layer.effect === eff.id ? "layer-effect-btn-active" : ""}`}
									onClick={() => handleLayerEffectChange({ ...layer, effect: eff.id })}
								>
									{eff.name}
								</button>
							))}
						</div>
					</div>

					{/* Effect Strength */}
					{layer.effect !== 0 && (
						<div className="layer-row">
							<span className="layer-label">Strength</span>
							<ModalNumberInput
								value={layer.effectStrength ?? 0}
								min={0}
								max={255}
								step={5}
								title={`Layer ${index} Effect Strength`}
								onApply={(next) => handleLayerEffectChange({ ...layer, effectStrength: Math.round(next) })}
							/>
						</div>
					)}

					{/* Effect Speed */}
					{layer.effect !== 0 && (
						<div className="layer-row">
							<span className="layer-label">Speed</span>
							<ModalNumberInput
								value={layer.effectSpeed ?? 1}
								min={1}
								max={100}
								step={5}
								title={`Layer ${index} Effect Speed (ms)`}
								onApply={(next) => handleLayerEffectChange({ ...layer, effectSpeed: Math.round(next) })}
							/>
						</div>
					)}
				</div>
			)}

			{/* Color Picker Modal */}
			{colorPickerOpen && (
				<ModalColorPicker
					color={currentColor}
					title={`Layer ${index} Color`}
					onColorChange={handleColorChange}
					onClose={() => setColorPickerOpen(false)}
				/>
			)}
		</div>
	);
}

export default LayerControl;
